'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBreakpointValue, Box, Flex, Text } from '@skin-seoul/design-system';
import { useEventListener } from '@skin-seoul/utils';
import { useReviewService } from '@skin-seoul/service';
import type { Review as ServiceReview } from '@skin-seoul/service';
import ToggleIcon from './assets/icons/ToggleIcon';
import { usePageService } from '@skin-seoul/service';
import { Spinner } from './components/common/Spinner';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import ReviewPagination from './components/ReviewPagination';
import type { ReviewFormData } from './components/common/interfaces';
import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';
import { ImageGalleryModal } from './components/ImageGalleryModal';
import { cleanSkinData } from './utils/taxonomyUtils';
import type { ComponentProps } from 'react';

type BoxProps = ComponentProps<typeof Box>;
type FlexProps = ComponentProps<typeof Flex>;

const Container = ({ children, ...props }: { children: React.ReactNode } & Omit<BoxProps, 'children'>) => (
  <Box
    w="full"
    bg="#ffffff"
    fontFamily="Plus Jakarta Sans, sans-serif"
    boxSizing="border-box"
    maxW={{ base: 'full', tablet: '1280px' }}
    mx={{ base: '0', tablet: 'auto' }}
    {...props}
  >
    {children}
  </Box>
);

const TitleWrapper = ({
  children,
  onClick,
  ...props
}: { children: React.ReactNode; onClick: () => void } & Omit<FlexProps, 'children' | 'onClick'>) => (
  <Flex align="center" justify="space-between" cursor="pointer" onClick={onClick} {...props}>
    {children}
  </Flex>
);

const ContentWrapper = ({
  children,
  isCollapsed,
  ...props
}: { children: React.ReactNode; isCollapsed: boolean } & Omit<BoxProps, 'children'>) => (
  <Box maxH={isCollapsed ? '0' : '10000px'} overflow="hidden" transition="max-height 0.3s ease-in-out" {...props}>
    {children}
  </Box>
);

const Divider = ({ ...props }: BoxProps) => (
  <Box border="none" h="1px" bg="#e2e2e2" my={{ base: '24px', tablet: '32px' }} {...props} />
);

const WriteReviewButton = ({
  children,
  onClick,
  disabled,
  ...props
}: { children: React.ReactNode; onClick: () => void; disabled?: boolean } & Omit<
  BoxProps,
  'children' | 'onClick' | 'disabled'
>) => (
  <Box
    as="button"
    mt="48px"
    w="full"
    h="48px"
    bg="#3d1690"
    color="#ffffff"
    border="none"
    borderRadius="8px"
    style={{ fontSize: '14px' }}
    fontWeight="400"
    cursor="pointer"
    fontFamily="Plus Jakarta Sans, sans-serif"
    display="block"
    mx="auto"
    maxW={{ base: 'full', tablet: '530px' }}
    _disabled={{ opacity: 0.5 }}
    _hover={{ opacity: 0.9 }}
    onClick={onClick}
    {...(disabled && { disabled: true })}
    {...props}
  >
    {children}
  </Box>
);

const ReviewList = ({
  children,
  singleReview,
  ...props
}: { children: React.ReactNode; singleReview?: boolean } & Omit<BoxProps, 'children'>) => (
  <Box
    display={{ base: 'block', tablet: singleReview ? 'block' : 'flex' }}
    gap={{ base: '0', tablet: '0px 32px' }}
    position="relative"
    alignItems="start"
    _before={{
      content: '""',
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '1px',
      height: '100%',
      bg: '#e2e2e2',
      zIndex: 1,
      display: {
        base: 'none',
        tablet: singleReview ? 'none' : 'block',
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

const Column = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex flex="1" direction="column" {...props}>
    {children}
  </Flex>
);

export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

export interface ReviewSectionProps {
  productId?: string;
}

function mapServiceReviewToReview(r: ServiceReview): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '',
      name: r.name,
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: r.modifiedAt,
    likeCount: 0,
    skinType: cleanSkinData(r.skinType),
    skinConcern: cleanSkinData(r.skinConcern),
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  };
}
export const ReviewSection = ({ productId }: ReviewSectionProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useBreakpointValue({ base: false, tablet: true });
  const [isCollapsed, setIsCollapsed] = useState(false);

  const reviewService = useReviewService();
  const { useGetProductReviews, useCreateReview } = reviewService;

  const pageService = usePageService();
  const { useGetPageMeta } = pageService;

  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  const { data: pageMeta, isLoading: isPageMetaLoading } = useGetPageMeta(
    { pathname: typeof window !== 'undefined' ? window.location.pathname : '' },
    { enabled: !isCollapsed && typeof window !== 'undefined' },
  );

  const postId: number | undefined = (() => {
    if (productId) {
      return Number(productId);
    }
    if (pageMeta?.postId) {
      return Number(pageMeta.postId);
    }
    return undefined;
  })();

  const { data: reviewsRaw = [], isLoading: isReviewsLoading } = useGetProductReviews(postId || 0);
  const reviews = reviewsRaw.map(mapServiceReviewToReview);

  const customerImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  );

  const sortedAndFilteredReviews = useMemo(() => {
    const baseReviews = reviews || [];

    const sortedReviews = (() => {
      switch (sort) {
        case 'newest':
          return [...baseReviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        case 'oldest':
          return [...baseReviews].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case 'highest':
          return [...baseReviews].sort((a, b) => b.rating - a.rating);
        case 'lowest':
          return [...baseReviews].sort((a, b) => a.rating - b.rating);
        default:
          return baseReviews;
      }
    })();

    const result = sortedReviews
      .filter((review) => skinTypes.length === 0 || skinTypes.some((type) => review.skinType.includes(type)))
      .filter(
        (review) => skinConcerns.length === 0 || skinConcerns.some((concern) => review.skinConcern.includes(concern)),
      );

    return result;
  }, [reviews, sort, skinTypes, skinConcerns]);

  const [itemsPerPage, setItemsPerPage] = useState(8);

  const calculateItemsPerPage = useCallback(() => {
    if (typeof window === 'undefined') {
      return 4;
    }

    const viewportHeight = window.innerHeight;
    const headerHeight = 200;
    const footerHeight = 150;
    const availableHeight = viewportHeight - headerHeight - footerHeight;

    const avgReviewWithImagesHeight = 300;
    const avgReviewWithoutImagesHeight = 180;

    const allReviews = reviews || [];
    const reviewsWithImages = allReviews.filter((r) => r.images && r.images.length > 0).length;
    const reviewsWithoutImages = allReviews.length - reviewsWithImages;

    const avgReviewHeight =
      allReviews.length > 0
        ? (reviewsWithImages * avgReviewWithImagesHeight + reviewsWithoutImages * avgReviewWithoutImagesHeight) /
          allReviews.length
        : 200;

    const itemsPerColumn = Math.floor(availableHeight / avgReviewHeight);
    const totalItems = Math.max(6, Math.min(16, itemsPerColumn * 2));

    return totalItems;
  }, [reviews]);

  const updateItemsPerPage = useCallback(() => {
    const newItemsPerPage = calculateItemsPerPage();
    setItemsPerPage(newItemsPerPage);
  }, [calculateItemsPerPage]);

  useEffect(() => {
    updateItemsPerPage();
  }, [updateItemsPerPage]);

  useEventListener('resize', updateItemsPerPage);

  useEffect(() => {
    if (!isReviewsLoading && reviews.length > 0) {
      const newItemsPerPage = calculateItemsPerPage();
      setItemsPerPage(newItemsPerPage);
    }
  }, [isReviewsLoading, reviews.length, calculateItemsPerPage]);

  const totalReviewsLength = reviews.length;
  const filteredTotalPages = Math.ceil((sortedAndFilteredReviews?.length || 0) / itemsPerPage);
  const averageRating =
    totalReviewsLength === 0
      ? 0
      : Number((reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength).toFixed(1));

  const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const { currentReviews, leftColumnReviews, rightColumnReviews } = useMemo(() => {
    const slicedReviews = sortedAndFilteredReviews?.slice(startIndex, endIndex) || [];
    const leftColumn: Review[] = [];
    const rightColumn: Review[] = [];

    slicedReviews.forEach((review, index) => {
      if (index % 2 === 0) {
        leftColumn.push(review);
      } else {
        rightColumn.push(review);
      }
    });

    return {
      currentReviews: slicedReviews,
      leftColumnReviews: leftColumn,
      rightColumnReviews: rightColumn,
    };
  }, [sortedAndFilteredReviews, startIndex, endIndex]);

  const hasReviews = reviews.length > 0;
  const productImage = pageMeta?.image || '';

  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleOpenWriteModal = () => {
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleCloseWriteModal = useCallback(() => setIsPcWriteModalOpen(false), []);

  const handleCloseMobileWriteModal = useCallback(() => setIsMobileWriteOpen(false), []);

  const handleSubmit = useCallback(
    (values: ReviewFormData) => {
      return new Promise<void>((resolve, reject) => {
        createReview(
          {
            name: values.nickname,
            rating: values.rating,
            body: values.content,
            productId: postId || 0,
            title: values.title,
            files: values.images,
            skinType: [values.skinType],
            skinConcern: [values.skinConcern],
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: (error) => {
              reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
            },
          },
        );
      });
    },
    [createReview, postId],
  );

  const handleSortChange = (sortBy: string) => {
    setCurrentPage(1);
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    setCurrentPage(1);
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    setCurrentPage(1);
    setSkinConcerns(skinConcerns);
  };

  const handleViewAllImages = () => {
    setIsImageGalleryOpen(true);
  };

  const handleCloseImageGallery = () => {
    setIsImageGalleryOpen(false);
  };

  return (
    <Container id="judgeme_product_reviews">
      <TitleWrapper onClick={() => setIsCollapsed(!isCollapsed)}>
        <Text
          textStyle={{ base: 'label.large', tablet: 'heading.h6' }}
          fontWeight="medium"
          color="neutral.800"
          textAlign="left"
          whiteSpace="nowrap"
          letterSpacing={{ tablet: '-0.4px' }}
        >
          Reviews
        </Text>
        <ToggleIcon isCollapsed={isCollapsed} />
      </TitleWrapper>

      <ContentWrapper isCollapsed={isCollapsed}>
        <ReviewHeader
          averageRating={averageRating}
          totalReviews={totalReviewsLength}
          ratingDistribution={ratingDistribution}
          customerImages={customerImages}
          reviews={reviews}
          onViewAllImages={handleViewAllImages}
        />

        {hasReviews && !isPageMetaLoading && !isReviewsLoading && (
          <>
            <Divider />

            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />

            <ReviewList singleReview={currentReviews.length === 1} alignSelf="start">
              {currentReviews.length === 1 ? (
                <ReviewCard
                  key={currentReviews[0].id}
                  review={currentReviews[0]}
                  onImageClick={handleViewAllImages}
                  isLastInColumn={true}
                />
              ) : (
                <>
                  <Column>
                    {leftColumnReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        onImageClick={handleViewAllImages}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </Column>
                  <Column>
                    {rightColumnReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        onImageClick={handleViewAllImages}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </Column>
                </>
              )}
            </ReviewList>

            <Divider margin="16px 0" />

            <ReviewPagination currentPage={currentPage} totalPages={filteredTotalPages} onPageChange={setCurrentPage} />
          </>
        )}
        {(isPageMetaLoading || isReviewsLoading) && <Spinner />}

        <WriteReviewButton onClick={handleOpenWriteModal} disabled={isCreatingReview}>
          Write a review
        </WriteReviewButton>
      </ContentWrapper>

      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productImage={productImage}
          onClose={handleCloseWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productImage={productImage}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleSubmit}
        />
      )}

      <ImageGalleryModal isOpen={isImageGalleryOpen} onClose={handleCloseImageGallery} images={customerImages} />
    </Container>
  );
};

export default ReviewSection;
